import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught unhandled UI error:', error, errorInfo);

    // Auto-reload once on ChunkLoadError
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Loading chunk [\w-]+ failed/i.test(error?.message || '') ||
      /Failed to fetch dynamically imported module/i.test(error?.message || '');

    if (isChunkError && typeof window !== 'undefined') {
      const alreadyReloaded = window.sessionStorage.getItem('ns_eb_chunk_reload');
      if (!alreadyReloaded) {
        window.sessionStorage.setItem('ns_eb_chunk_reload', 'true');
        window.location.reload();
      }
    }
  }

  handleReload = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem('ns_eb_chunk_reload');
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isChunkError =
        this.state.error?.name === 'ChunkLoadError' ||
        /Loading chunk [\w-]+ failed/i.test(this.state.error?.message || '');

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '380px',
            padding: '2rem',
            textAlign: 'center',
            background: 'var(--ns-surface, #ffffff)',
            borderRadius: 'var(--ns-radius-card, 14px)',
            border: '1px solid var(--ns-border-default, #e2e8f0)',
            margin: '2rem auto',
            maxWidth: '560px',
            boxShadow: 'var(--ns-shadow-card, 0 2px 8px rgba(0,0,0,0.04))',
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: 'var(--ns-negative-subtle, #fef2f2)',
              color: 'var(--ns-negative-text, #dc2626)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem',
            }}
          >
            <AlertTriangle size={24} />
          </div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ns-text-primary, #0f172a)', margin: '0 0 0.5rem' }}>
            {isChunkError ? 'Application Update Available' : 'Something went wrong'}
          </h2>
          <p style={{ fontSize: '0.875rem', color: 'var(--ns-text-tertiary, #64748b)', margin: '0 0 1.5rem', lineHeight: 1.5 }}>
            {isChunkError
              ? 'A fresh update has been compiled. Please reload the page to load the latest components.'
              : 'An unexpected error occurred while rendering this page.'}
          </p>
          <button
            type="button"
            className="ns-btn ns-btn--primary"
            onClick={this.handleReload}
            style={{ minHeight: 38, padding: '0 1.25rem', display: 'inline-flex', alignItems: 'center', gap: 8 }}
          >
            <RefreshCw size={15} />
            <span>Reload Page</span>
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
